/**
 * Sprint V100 — Dedizierte QuickCheck-Landingpage fuer Cold-Traffic.
 * V100.3 — Free-Registration-CTA via LoginModal (Magic-Link).
 *
 * V101 (Mo 21.09.2026, 14:19) — Auth-Weiche eingebaut:
 *   Wenn ein bereits eingeloggter Free-User hierher landet (z.B. via
 *   testRouting weil noch kein primary_fokustyp gesetzt), wird das Ergebnis
 *   direkt auf seinen Account persistiert (Backend erkennt Auth-Cookie) und
 *   der Follow-up-Block zeigt „Zurueck zum Cockpit" statt einer Magic-Link-
 *   Registrierungs-Schleife.
 *
 * URL: /quickcheck
 * Zielgruppe: Ads-Traffic (anonym) + Free-User ohne Fokustyp (eingeloggt).
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import QuickCheckV100 from "../components/quickcheck/QuickCheckV100";
import LoginModal from "../components/LoginModal";
import { useAuth } from "@/context/AuthContext";

export default function QuickCheckLanding() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [magicOpen, setMagicOpen] = useState(false);
  const [magicSource, setMagicSource] = useState("");
  const [magicFokustyp, setMagicFokustyp] = useState(null);

  // user === null   → Auth-Check laeuft noch (fetchMe pending)
  // user === false  → nicht eingeloggt (Cold-Traffic)
  // user === object → eingeloggt (Free/Pro-User)
  const isAuthenticated = !!user && typeof user === "object";

  const handleMagicLink = (source, fokustyp) => {
    setMagicSource(source || "quickcheck_lp_result");
    setMagicFokustyp(fokustyp || null);
    setMagicOpen(true);
  };

  const handleBackToCockpit = () => {
    navigate("/dashboard");
  };

  // Auth-abhaengige Hero-Copy: eingeloggter User bekommt kontextpassenden
  // Kicker + Trust-Line („dein Ergebnis wird ins Cockpit gespeichert"),
  // anonymer User weiterhin das „kein Login, keine E-Mail"-Framing.
  const kicker = isAuthenticated
    ? "Fokustyp bestimmen · 5 Fragen · 2 Minuten"
    : "Kostenfreier QuickCheck · 5 Fragen · 2 Minuten";
  const trustLine = isAuthenticated
    ? "Dein Ergebnis wird direkt in deinem Mitgliederbereich gespeichert — kein weiterer Schritt nötig."
    : "Kein Login. Keine E-Mail. Kein Datensammeln — nur du und dein Ergebnis.";

  return (
    <div
      className="min-h-screen bg-slate-50 dark:bg-slate-950"
      data-testid="quickcheck-landing"
      data-auth-state={isAuthenticated ? "authenticated" : "anonymous"}
    >
      {/* ==================== HERO ==================== */}
      <section className="relative overflow-hidden">
        {/* Dezenter 4-Felder-Matrix-Hintergrund (kanonisch: Katze/Eule/Hund/Biene) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 grid grid-cols-2 grid-rows-2 select-none"
        >
          {[
            { key: "katze", src: "/icons/icon-katze.png", pos: "items-start justify-start p-6 sm:p-12" },
            { key: "eule", src: "/icons/icon-eule.png", pos: "items-start justify-end p-6 sm:p-12" },
            { key: "hund", src: "/icons/icon-hund.png", pos: "items-end justify-start p-6 sm:p-12" },
            { key: "biene", src: "/icons/icon-biene.png", pos: "items-end justify-end p-6 sm:p-12" },
          ].map((t) => (
            <div key={t.key} className={`flex ${t.pos}`}>
              <img
                src={t.src}
                alt=""
                aria-hidden="true"
                className="w-24 sm:w-36 lg:w-48 h-auto opacity-[0.06] dark:opacity-[0.08]"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-10 text-center">
          <p className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-[#F3651D]">
            {kicker}
          </p>
          <h1
            className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-[1.1]"
            data-testid="quickcheck-landing-title"
          >
            Erkenne dein Muster.<br />
            Verstehe deine Wirkung.
          </h1>
          <p
            className="mt-5 text-base sm:text-lg text-slate-700 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed"
            data-testid="quickcheck-landing-subtitle"
          >
            Fünf Situationen, eine ehrliche Selbsteinschätzung. Danach weißt du,
            welcher Fokustyp dich prägt — und wo deine Zweitfarbe mitspielt.
          </p>
          <p
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-900/[0.04] dark:bg-white/[0.06] text-slate-700 dark:text-slate-300 px-3 py-1.5 text-xs font-medium"
            data-testid="quickcheck-landing-trust"
          >
            {trustLine}
          </p>
        </div>
      </section>

      {/* ==================== TEST ==================== */}
      <section className="relative pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <QuickCheckV100
            source={isAuthenticated ? "dashboard_test" : "quickcheck_lp"}
            onMagicLink={isAuthenticated ? undefined : handleMagicLink}
            onBackToCockpit={isAuthenticated ? handleBackToCockpit : undefined}
          />
        </div>
      </section>

      {/* ==================== FOOTER-TRUST ==================== */}
      <footer className="border-t border-slate-200 dark:border-slate-800 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-xs text-slate-500 dark:text-slate-400">
          Datensparsam gebaut nach DSGVO. &middot;{" "}
          <a href="/datenschutz" className="underline hover:text-slate-700 dark:hover:text-slate-200">
            Datenschutz
          </a>{" "}
          &middot;{" "}
          <a href="/impressum" className="underline hover:text-slate-700 dark:hover:text-slate-200">
            Impressum
          </a>
        </div>
      </footer>

      {/* Modal nur fuer anonyme User relevant — bleibt geschlossen bei Auth. */}
      {!isAuthenticated && (
        <LoginModal
          key={magicOpen ? `open-${magicSource}-${magicFokustyp || "none"}` : "closed"}
          open={magicOpen}
          onClose={() => setMagicOpen(false)}
          source={magicSource}
          fokustyp={magicFokustyp}
        />
      )}
    </div>
  );
}
